 
import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  ShoppingCart, Plus, Minus, Trash2, Tag, Loader2,
  MapPin, Phone, ChevronDown, X, CheckCircle2, Search, Star, Sparkles
} from 'lucide-react';
import { deliveryApi, getUnitPriceForQuantity, type CatalogCategory } from '../api/delivery';
import { useCartStore } from '../store/cart';
import { getBranding, fmt, type Branding } from '../lib/branding';
import { getStoredReferralCode } from '../lib/referral';
import { ProductCard } from '../components/ProductCard';
import { getCartUpsellProducts } from '../lib/upsell';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Quantos produtos mostrar antes de colapsar a categoria atrás de "ver mais".
const COLLAPSE_THRESHOLD = 8;

function fmtPhone(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (d.length === 13) return `+${d.slice(0,2)} (${d.slice(2,4)}) ${d.slice(4,9)}-${d.slice(9)}`;
  if (d.length === 11) return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7)}`;
  return raw;
}

// ─── Cart FAB ─────────────────────────────────────────────────────────────────

function CartFAB({ brand, onClick }: { brand: Branding; onClick: () => void }) {
  const { items } = useCartStore();
  // BUGFIX (11/07/2026): quantity é armazenado em centésimos (100 = 1 un,
  // ver store/cart.ts) -- precisa dividir por 100 pra exibir/calcular certo,
  // senão "1 unidade" aparecia como "100" (achado real: Thales reportou
  // "marcar um produto puxa 100").
  const count = items.reduce((s, i) => s + i.quantity / 100, 0);
  const total = items.reduce((s, i) => {
    const qty = i.quantity / 100;
    return s + getUnitPriceForQuantity(i.product, qty) * qty;
  }, 0);
  if (count === 0) return null;
  return (
    <button
      onClick={onClick}
      style={{ backgroundColor: brand.primaryColor }}
      // `fixed` ignora a moldura max-width do container -- centraliza via
      // left-0/right-0/mx-auto (largura do próprio conteúdo, w-fit) em vez de
      // left-1/2 (que centralizava na tela inteira, não na moldura).
      className="fixed bottom-5 left-0 right-0 mx-auto w-fit z-40 flex items-center gap-3 text-white font-semibold px-5 py-3 rounded-full shadow-2xl transition-transform active:scale-95 text-sm"
    >
      <ShoppingCart size={18} />
      <span>{count} {count === 1 ? 'item' : 'itens'}</span>
      <span className="opacity-75">·</span>
      <span>{fmt(total)}</span>
    </button>
  );
}

// ─── Product Card ─────────────────────────────────────────────────────────────

// ─── Cart Sheet ───────────────────────────────────────────────────────────────

function CartSheet({ open, brand, onClose, onCheckout, categories, categoryAffinity }: {
  open: boolean;
  brand: Branding;
  onClose: () => void;
  onCheckout: () => void;
  categories: CatalogCategory[];
  categoryAffinity: Record<string, string[]> | undefined;
}) {
  const { items, updateQuantity, removeItem } = useCartStore();
  const total = items.reduce((s, i) => {
    const qty = i.quantity / 100;
    return s + getUnitPriceForQuantity(i.product, qty) * qty;
  }, 0);

  // 13/07/2026 (a pedido do Thales: "pra ter upsell a pessoa teria que
  // entrar na página do produto, mas do jeito que tá não tem como" --
  // observação certa: "Adicionar" direto no grid nunca passa pela
  // ProductPage. O carrinho é o único ponto que todo cliente vê antes de
  // fechar o pedido, então o rail de sugestão entra aqui também -- ver
  // getCartUpsellProducts em upsell.ts.
  const cartUpsell = useMemo(() => {
    if (items.length === 0) return [];
    const cartProductIds = new Set(items.map((i) => i.product.id));
    return getCartUpsellProducts(categories, cartProductIds, categoryAffinity);
  }, [items, categories, categoryAffinity]);

  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 mx-auto max-w-[480px] z-50 bg-white rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
          <h2 className="text-lg font-bold text-gray-900">Seu carrinho</h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        <div className="flex-1 min-h-[120px] overflow-y-auto px-5 divide-y divide-gray-100">
          {items.map((item) => (
            <div key={item.product.id} className="flex items-center gap-3 py-3">
              {item.product.imageUrl
                ? <img src={item.product.imageUrl} alt={item.product.name} className="w-14 h-14 rounded-xl object-cover shrink-0" />
                : <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center shrink-0"><svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>
              }
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{item.product.name}</p>
                <p style={{ color: brand.accentColor }} className="text-sm font-bold">
                  {fmt(getUnitPriceForQuantity(item.product, item.quantity / 100) * (item.quantity / 100))}
                </p>
                {item.product.bulkPricing && item.product.bulkPricing.length > 0 && item.quantity > 100 && (
                  <p className="text-[11px] text-green-700 font-semibold">
                    {fmt(getUnitPriceForQuantity(item.product, item.quantity / 100))}/un com desconto de quantidade
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-2 py-1 shrink-0">
                <button
                  onClick={() => item.quantity === 100 ? removeItem(item.product.id) : updateQuantity(item.product.id, item.quantity - 100)}
                  style={{ color: brand.primaryColor }}
                  className="p-0.5"
                >
                  {item.quantity === 100 ? <Trash2 size={14} /> : <Minus size={14} />}
                </button>
                <span className="text-sm font-bold w-5 text-center">{item.quantity / 100}</span>
                <button
                  onClick={() => updateQuantity(item.product.id, item.quantity + 100)}
                  style={{ color: brand.primaryColor }}
                  className="p-0.5"
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Sugestões no carrinho (ver comentário no cartUpsell acima) --
            reusa ProductCard sem onOpen: só o botão "Adicionar" funciona,
            não navega pra ProductPage (manteria a sheet aberta é o
            comportamento certo aqui). */}
        {cartUpsell.length > 0 && (
          <div className="px-5 pt-3 pb-1 border-t border-gray-100 min-h-0 overflow-y-auto">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
              Adicionar ao pedido
            </h3>
            <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
              {cartUpsell.map((p) => (
                <div key={p.id} className="w-32 shrink-0">
                  <ProductCard product={p} brand={brand} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="px-5 pb-6 pt-4 border-t border-gray-100 space-y-3 shrink-0">
          <div className="flex items-center justify-between">
            <span className="text-gray-600 text-sm">Total</span>
            <span className="text-lg font-bold text-gray-900">{fmt(total)}</span>
          </div>
          <button
            onClick={onCheckout}
            style={{ backgroundColor: brand.primaryColor }}
            className="w-full text-white font-semibold py-3.5 rounded-2xl text-base active:opacity-90 transition-opacity"
          >
            Finalizar pedido
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Checkout Modal ───────────────────────────────────────────────────────────

// 12/07/2026 (a pedido do Thales, "desativa por hora o pix da assas só
// maquininha na entrega"): espelha ASAAS_PIX_ONLINE_ENABLED em
// delivery.service.ts -- muda os dois juntos quando for reativar.
const PIX_ONLINE_ENABLED = false;

interface CheckoutForm {
  customerName: string;
  customerPhone: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
  zipcode: string;
  notes: string;
  promoCode: string;
}

function CheckoutModal({ open, brand, slug, onClose, onSuccess }: {
  open: boolean;
  brand: Branding;
  slug: string;
  onClose: () => void;
  onSuccess: (orderId: string) => void;
}) {
  const { items, clear: clearCart } = useCartStore();
  const [form, setForm] = useState<CheckoutForm>({
    customerName: '', customerPhone: '', customerCpf: '', street: '', number: '',
    complement: '', neighborhood: 'Centro', city: brand.addressLine?.split(' - ')[0] ?? '',
    state: brand.addressLine?.split(' - ')[1] ?? '', zipcode: '', notes: '', promoCode: '',
  });
  const [promoMsg, setPromoMsg] = useState<string | null>(null);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<string>('PIX');
  const [pixData, setPixData] = useState<{ pixQrCodeBase64: string; pixCopiaECola: string; expiresAt: string; paymentId: string } | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [pixCopied, setPixCopied] = useState(false);
  const [loyaltyInfo, setLoyaltyInfo] = useState<{ points: number; name: string; available: Array<{ id: string; label: string; pointsRequired: number }>; upcoming: Array<{ id: string; label: string; pointsRequired: number; missing: number }> } | null>(null);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const set = (k: keyof CheckoutForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  // Zona de entrega -- 12/07/2026, a pedido do Thales ("não tá colocando
  // taxa de entrega"): o checkout só coletava endereço em texto livre, sem
  // nenhuma forma de resolver lat/lng ou zona, então todo pedido saía com
  // deliveryFeeCents = 0 (o backend só calcula a taxa quando recebe zoneId
  // OU lat/lng -- nunca tinha nenhum dos dois). Reaproveita
  // store.deliveryZones (mesmo cache ['store', slug] já usado pelo resto da
  // página) pra deixar o cliente escolher a área -- muito mais simples que
  // pedir localização numa cidade pequena com poucas zonas cadastradas.
  const { data: store } = useQuery({
    queryKey: ['store', slug],
    queryFn: () => deliveryApi.getStoreInfo(slug),
    enabled: !!slug,
  });
  const zones = store?.deliveryZones ?? [];
  const [zoneId, setZoneId] = useState<string | null>(null);
  useEffect(() => {
    if (zoneId || zones.length === 0) return;
    const centro = zones.find((z) => /centro/i.test(z.name) && !/maior/i.test(z.name)) ?? zones[0];
    setZoneId(centro.id);
  }, [zones, zoneId]);
  const selectedZone = zones.find((z) => z.id === zoneId);

  // Lookup loyalty points when phone has 10+ digits
  async function handlePhoneChange(e: React.ChangeEvent<HTMLInputElement>) {
    set('customerPhone')(e);
    const digits = e.target.value.replace(/\D/g, '');
    if (digits.length >= 10) {
      setLoyaltyLoading(true);
      try {
        const info = await deliveryApi.getLoyaltyByPhone(slug, digits);
        setLoyaltyInfo(info);
      } catch { setLoyaltyInfo(null); }
      finally { setLoyaltyLoading(false); }
    } else {
      setLoyaltyInfo(null);
    }
  }

  const validatePromo = useMutation({
    mutationFn: () => deliveryApi.validatePromo(slug, form.promoCode),
    onSuccess: (data) => {
      setPromoMsg(`✅ ${data.description ?? 'Cupom válido!'}`);
      setPromoDiscount(data.discountCents ?? 0);
    },
    onError: () => { setPromoMsg('❌ Cupom inválido'); setPromoDiscount(0); },
  });

  const createOrder = useMutation({
    mutationFn: () => deliveryApi.createOrder(slug, {
      customerName: form.customerName,
      customerPhone: form.customerPhone.replace(/\D/g, ''),
      customerCpf: form.customerCpf ? form.customerCpf.replace(/\D/g, '') : undefined,
      address: {
        street: form.street, number: form.number,
        complement: form.complement || undefined,
        neighborhood: form.neighborhood, city: form.city,
        state: form.state, zipcode: form.zipcode ? form.zipcode.replace(/\D/g, '') : undefined,
      },
      zoneId: zoneId ?? undefined,
      items: items.map((i) => ({ productId: i.product.id, quantity: i.quantity })),
      promoCode: form.promoCode || undefined,
      referralCode: getStoredReferralCode(slug),
      notes: form.notes || undefined,
      paymentMethod,
    }),
    onSuccess: (data) => {
      if (paymentMethod === 'PIX' && data.pix) {
        setPixData(data.pix);
        setOrderId(data.orderId ?? data.id);
      } else {
        clearCart();
        onSuccess(data.orderId ?? data.id);
      }
    },
  });

  const subtotal = items.reduce((s, i) => {
    const qty = i.quantity / 100;
    return s + getUnitPriceForQuantity(i.product, qty) * qty;
  }, 0);

  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
        <div className="bg-white w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl max-h-[92vh] flex flex-col">
          <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900">Finalizar pedido</h2>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100">
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {/* Identificação */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Seus dados</p>
              <div className="space-y-2">
                <input placeholder="Seu nome *" value={form.customerName} onChange={set('customerName')}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400" />
                <input placeholder="WhatsApp (DDD + número) *" value={form.customerPhone} onChange={handlePhoneChange}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400" />
              </div>
            </div>
            {/* Loyalty info */}
            {loyaltyLoading && (
              <div className="flex items-center gap-2 text-xs text-gray-400 py-1">
                <Loader2 size={12} className="animate-spin" /> Consultando pontos...
              </div>
            )}
            {loyaltyInfo && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Star size={14} className="text-amber-500 fill-amber-500" />
                  <p className="text-sm font-semibold text-amber-800">
                    {loyaltyInfo.name} — <span className="text-amber-600">{loyaltyInfo.points} pontos</span>
                  </p>
                </div>
                {loyaltyInfo.available.length > 0 && (
                  <div className="mb-1">
                    <p className="text-xs font-semibold text-green-700 mb-1">🎁 Recompensas disponíveis:</p>
                    {loyaltyInfo.available.map(r => (
                      <p key={r.id} className="text-xs text-green-700">• {r.label}</p>
                    ))}
                  </div>
                )}
                {loyaltyInfo.upcoming.length > 0 && (
                  <div>
                    <p className="text-xs text-amber-600">
                      Próxima recompensa em <strong>{loyaltyInfo.upcoming[0].missing} ponto{loyaltyInfo.upcoming[0].missing !== 1 ? 's' : ''}</strong>: {loyaltyInfo.upcoming[0].label}
                    </p>
                  </div>
                )}
                <p className="text-xs text-amber-500 mt-1.5">+1 ponto ao concluir este pedido ⭐</p>
              </div>
            )}
            {/* Endereço */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Endereço de entrega</p>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input placeholder="Rua / Av. *" value={form.street} onChange={set('street')}
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400" />
                  <input placeholder="Nº *" value={form.number} onChange={set('number')}
                    className="w-20 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400" />
                </div>
                <input placeholder="Complemento" value={form.complement} onChange={set('complement')}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400" />
                <input placeholder="Bairro *" value={form.neighborhood} onChange={set('neighborhood')}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400" />
                <div className="flex gap-2">
                  <input placeholder="Cidade *" value={form.city} onChange={set('city')}
                    className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400" />
                  <input placeholder="UF *" value={form.state} onChange={set('state')} maxLength={2}
                    className="w-16 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400 uppercase" />
                </div>
                <input placeholder="CEP (opcional)" value={form.zipcode} onChange={set('zipcode')}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400" />
              </div>
            </div>
            {/* Área de entrega -- define a taxa cobrada; sem isso o pedido
                saía sempre com taxa R$0 */}
            {zones.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Área de entrega</p>
                <div className="grid grid-cols-2 gap-2">
                  {zones.map((z) => (
                    <button
                      key={z.id}
                      onClick={() => setZoneId(z.id)}
                      style={zoneId === z.id ? { borderColor: brand.primaryColor, backgroundColor: brand.primaryColor + '15' } : {}}
                      className={`border-2 rounded-xl p-2.5 text-left transition-colors ${
                        zoneId === z.id ? '' : 'border-gray-200'
                      }`}
                    >
                      <p className="text-sm font-semibold text-gray-800">{z.name}</p>
                      <p className="text-xs text-gray-400">{fmt(z.feeCents)}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* Cupom */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cupom</p>
              <div className="flex gap-2">
                <input placeholder="Código do cupom" value={form.promoCode} onChange={set('promoCode')}
                  className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400 uppercase" />
                <button
                  onClick={() => validatePromo.mutate()}
                  disabled={!form.promoCode || validatePromo.isPending}
                  style={{ backgroundColor: brand.primaryColor }}
                  className="px-4 text-white text-sm font-semibold rounded-xl disabled:opacity-40"
                >
                  <Tag size={16} />
                </button>
              </div>
              {promoMsg && <p className="text-xs mt-1 text-gray-600">{promoMsg}</p>}
            </div>
            {/* Observações */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Observações</p>
              <textarea placeholder="Alguma observação? (opcional)" value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} rows={2}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400 resize-none" />
            </div>
            {/* Resumo */}
            <div className="bg-gray-50 rounded-2xl p-4 space-y-1.5">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Subtotal</span><span>{fmt(subtotal)}</span>
              </div>
              {promoDiscount > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Desconto</span><span>-{fmt(promoDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs text-gray-400">
                <span>Taxa de plataforma</span><span>R$ 0,99</span>
              </div>
              {selectedZone && (
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Entrega ({selectedZone.name})</span><span>{fmt(selectedZone.feeCents)}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-bold text-gray-900 pt-1 border-t border-gray-200">
                <span>Total</span>
                <span>
                  {fmt(Math.max(0, subtotal - promoDiscount) + (selectedZone?.feeCents ?? 0))}
                  {!selectedZone && <span className="text-xs font-normal text-gray-400"> + entrega</span>}
                </span>
              </div>
            </div>
          </div>
          {createOrder.isError && (
            <div className="px-5 pb-2">
              <p className="text-xs text-red-500 text-center">Erro ao fazer pedido. Verifique os campos e tente novamente.</p>
            </div>
          )}
          <div className="px-5 pb-6 pt-3 border-t border-gray-100">
            {/* PIX QR Code screen */}
            {pixData && orderId ? (
              <div className="space-y-4 text-center">
                <div className="bg-green-50 rounded-2xl p-4">
                  <p className="text-sm font-semibold text-green-800 mb-1">✅ Pedido criado! Pague para confirmar</p>
                  <p className="text-xs text-green-600">Pedido #{orderId.slice(-6).toUpperCase()}</p>
                </div>
                <img
                  src={`data:image/png;base64,${pixData.pixQrCodeBase64}`}
                  alt="QR Code PIX"
                  className="w-52 h-52 mx-auto rounded-2xl border-4 border-gray-100"
                />
                <div>
                  <p className="text-xs text-gray-500 mb-1">PIX Copia e Cola</p>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={pixData.pixCopiaECola}
                      className="flex-1 border rounded-xl px-3 py-2 text-xs bg-gray-50 font-mono truncate"
                    />
                    <button
                      onClick={() => { navigator.clipboard.writeText(pixData.pixCopiaECola); setPixCopied(true); setTimeout(() => setPixCopied(false), 2000); }}
                      style={{ backgroundColor: brand.primaryColor }}
                      className="px-3 rounded-xl text-white text-xs font-semibold shrink-0"
                    >
                      {pixCopied ? '✓' : 'Copiar'}
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => { clearCart(); onSuccess(orderId); }}
                  style={{ backgroundColor: brand.primaryColor }}
                  className="w-full text-white font-bold py-3.5 rounded-2xl text-base"
                >
                  Já paguei — Ver meu pedido
                </button>
              </div>
            ) : (
              <>
                {/* Payment method selection */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Forma de pagamento</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: 'PIX', label: '⚡ PIX', sub: 'Na entrega' },
                      { id: 'CREDIT_CARD', label: '💳 Crédito', sub: 'Maquininha' },
                      { id: 'CASH_ON_DELIVERY', label: '💵 Dinheiro', sub: 'Na entrega' },
                      { id: 'DEBIT_ON_DELIVERY', label: '🔵 Débito', sub: 'Maquininha' },
                    ].map((opt) => (
                      <button
                        key={opt.id}
                        onClick={() => setPaymentMethod(opt.id)}
                        style={paymentMethod === opt.id ? { borderColor: brand.primaryColor, backgroundColor: brand.primaryColor + '15' } : {}}
                        className={`border-2 rounded-xl p-2.5 text-left transition-colors ${
                          paymentMethod === opt.id ? '' : 'border-gray-200'
                        }`}
                      >
                        <p className="text-sm font-semibold text-gray-800">{opt.label}</p>
                        <p className="text-xs text-gray-400">{opt.sub}</p>
                      </button>
                    ))}
                  </div>
                  {PIX_ONLINE_ENABLED && paymentMethod === 'PIX' && (
                    <div className="mt-2">
                      <input
                        placeholder="CPF (opcional — só pra pagar agora com QR code)"
                        value={form.customerCpf}
                        onChange={(e) => setForm((f) => ({ ...f, customerCpf: e.target.value.replace(/\D/g, '').slice(0, 11) }))}
                        inputMode="numeric"
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400"
                      />
                      <p className="text-[11px] text-gray-400 mt-1">
                        Sem CPF? Sem problema — confirma o pedido e paga o PIX na entrega, do jeito de sempre.
                      </p>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => createOrder.mutate()}
                  disabled={
                    createOrder.isPending ||
                    !form.customerName || !form.customerPhone ||
                    !form.street || !form.number || !form.neighborhood ||
                    !form.city || !form.state ||
                    (zones.length > 0 && !zoneId)
                  }
                  style={{ backgroundColor: brand.primaryColor }}
                  className="w-full text-white font-bold py-3.5 rounded-2xl text-base flex items-center justify-center gap-2 disabled:opacity-40 transition-opacity"
                >
                  {createOrder.isPending ? <><Loader2 size={18} className="animate-spin" />Processando...</> :
                    paymentMethod === 'PIX' ? '⚡ Confirmar Pedido (PIX na entrega)' :
                    paymentMethod === 'CREDIT_CARD' ? '💳 Confirmar Pedido (Cartão na entrega)' :
                    '✅ Confirmar Pedido'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Order Confirmed ──────────────────────────────────────────────────────────

function OrderConfirmed({ orderId, slug, brand, onClose }: { orderId: string; slug: string; brand: Branding; onClose: () => void }) {
  const { data: order } = useQuery({
    queryKey: ['order-status', orderId],
    queryFn: () => deliveryApi.getOrderStatus(slug!, orderId),
    refetchInterval: 15_000,
  });

  const STATUS_LABEL: Record<string, string> = {
    pending: '⏳ Aguardando confirmação',
    confirmed: '✅ Pedido confirmado!',
    preparing: '👨‍🍳 Preparando seu pedido',
    out_for_delivery: '🛵 Saiu para entrega',
    delivered: '🎉 Entregue!',
    cancelled: '❌ Cancelado',
  };

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col items-center justify-center gap-6 px-8 text-center">
      <CheckCircle2 size={72} style={{ color: brand.primaryColor }} />
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Pedido realizado!</h2>
        <p className="text-gray-500 text-sm">Acompanhe o status abaixo</p>
      </div>
      {order && (
        <div className="bg-gray-50 rounded-2xl px-6 py-4 w-full max-w-xs">
          <p className="text-base font-semibold text-gray-800">{STATUS_LABEL[order.status] ?? order.status}</p>
          <p className="text-xs text-gray-500 mt-1">Pedido #{orderId.slice(-6).toUpperCase()}</p>
        </div>
      )}
      {brand.whatsappNumber && (
        <a
          href={`https://wa.me/${brand.whatsappNumber}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ borderColor: brand.primaryColor, color: brand.primaryColor }}
          className="flex items-center gap-2 border-2 rounded-2xl px-5 py-2.5 text-sm font-semibold"
        >
          <Phone size={16} />
          Falar com a loja
        </a>
      )}
      <button onClick={onClose} className="text-sm text-gray-400 underline">Fazer novo pedido</button>
    </div>
  );
}

// ─── Store Page (main) ────────────────────────────────────────────────────────

export default function StorePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [confirmedOrderId, setConfirmedOrderId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const navRef = useRef<HTMLDivElement>(null);
  // 12/07/2026 (revisão de design): categorias grandes (algumas passam de
  // 30 produtos) faziam a página rolar demais antes do cliente ver a
  // próxima categoria -- colapsa por padrão além de COLLAPSE_THRESHOLD,
  // com botão 'ver mais'. Nunca colapsa durante busca (o resultado da
  // busca tem que aparecer inteiro, senão o cliente acha que não tem o
  // produto).
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const { data: store, isLoading: storeLoading, isError: storeError } = useQuery({
    queryKey: ['store', slug],
    queryFn: () => deliveryApi.getStoreInfo(slug!),
    enabled: !!slug,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const { data: catalog, isLoading: catalogLoading } = useQuery({
    queryKey: ['catalog', slug],
    queryFn: () => deliveryApi.getCatalog(slug!),
    enabled: !!slug,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const storeConfig = catalog?.store ?? null;
  const brand = store ? getBranding(store) : {
    primaryColor: '#2D6A2D', accentColor: '#8B6914',
    logoUrl: null, bannerUrl: null, description: null,
    addressLine: null, whatsappNumber: null,
    storeHours: null, deliveryTimeMin: null, deliveryTimeMax: null,
    deliveryFeeText: null, minimumOrderCents: null,
  };

  // Inject CSS var + dynamic SEO tags
  useEffect(() => {
    document.documentElement.style.setProperty('--color-brand', brand.primaryColor);
    if (!store) return;

    // Title
    const locationPart = store.storeCity && store.storeState
      ? ` · Delivery em ${store.storeCity} - ${store.storeState}`
      : ' · Delivery';
    document.title = `${store.name}${locationPart}`;

    // Helper to upsert meta tag
    const setMeta = (property: string, content: string, attr = 'property') => {
      let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, property);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    const desc = brand.description
      ?? `Peça online no ${store.name}. Delivery rápido${store.storeCity ? ` em ${store.storeCity}` : ''}.`;
    const imgUrl = brand.logoUrl
      ? (brand.logoUrl.startsWith('http') ? brand.logoUrl : `https://autodelivery.app.br${brand.logoUrl}`)
      : 'https://autodelivery.app.br/og-default.png';

    setMeta('og:title', document.title);
    setMeta('og:description', desc);
    setMeta('og:image', imgUrl);
    setMeta('og:url', window.location.href);
    setMeta('og:type', 'website');
    setMeta('description', desc, 'name');
    setMeta('twitter:card', 'summary_large_image', 'name');
    setMeta('twitter:title', document.title, 'name');
    setMeta('twitter:description', desc, 'name');
    setMeta('twitter:image', imgUrl, 'name');
  }, [brand.primaryColor, brand.description, brand.logoUrl, store]);

  // Category nav scroll
  const scrollToCategory = (id: string) => {
    setActiveCategory(id);
    categoryRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // scroll nav tab into view
    const btn = navRef.current?.querySelector(`[data-cat="${id}"]`);
    btn?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };

  const filteredCatalog = (catalog?.categories ?? []).map((cat) => ({
    ...cat,
    products: cat.products.filter((p) =>
      search === '' || p.name.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter((cat) => cat.products.length > 0);

  if (storeLoading || catalogLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={36} className="animate-spin text-gray-400" />
      </div>
    );
  }

  if (storeError || !store) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-8 text-center">
        <p className="text-4xl">🚫</p>
        <p className="text-lg font-semibold text-gray-800">Loja não encontrada</p>
        <p className="text-sm text-gray-500">Verifique o link ou entre em contato com a loja.</p>
      </div>
    );
  }

  if (confirmedOrderId) {
    return <OrderConfirmed orderId={confirmedOrderId} slug={slug!} brand={brand} onClose={() => setConfirmedOrderId(null)} />;
  }

  return (
    // Moldura max-width (12/07/2026): sem isso a grade de 2 colunas ficava
    // esparsa/estranha em tela larga (cards enormes, muito vazio no meio).
    // `fixed` (FAB, cart sheet, checkout) precisa do mesmo tratamento à
    // parte -- ver comentários nesses trechos.
    <div className="min-h-screen bg-gray-100">
    <div className="max-w-[480px] mx-auto min-h-screen bg-gray-50 pb-28 shadow-xl">
      {/* ── Hero ── */}
      <div
        className="relative w-full flex flex-col items-center justify-end pb-6 pt-16"
        style={{
          background: brand.bannerUrl
            // 12/07/2026 (revisão de design): escurecido de 0.15 pra 0.35 no
            // topo -- o scrim de baixo já garantia contraste pra descrição,
            // mas o topo (onde ficam logo + nome da loja) ficava claro
            // demais em fotos de banner mais claras/variadas. Não resolve
            // sozinho se a foto do banner já tiver texto "queimado" nela
            // (ex: um gráfico promocional com título embutido) -- nesse
            // caso o ideal é o dono subir uma foto sem texto embutido, já
            // que o nome da loja é renderizado por cima pelo próprio site.
            ? `linear-gradient(to bottom, rgba(0,0,0,0.35), rgba(0,0,0,0.6)), url(${brand.bannerUrl}) center/cover no-repeat`
            : `linear-gradient(135deg, ${brand.primaryColor} 0%, ${brand.accentColor} 100%)`,
          minHeight: '220px',
        }}
      >
        {/* Logo */}
        {brand.logoUrl ? (
          <img
            src={brand.logoUrl}
            alt={store.name}
            className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-xl mb-3"
          />
        ) : (
          <div
            className="w-24 h-24 rounded-full border-4 border-white shadow-xl mb-3 flex items-center justify-center text-5xl bg-white"
          >
            🏪
          </div>
        )}
        <h1 className="text-3xl font-extrabold text-white tracking-tight drop-shadow-md text-center px-6">{store.name}</h1>
        {brand.description && (
          <p className="text-white/80 text-sm mt-1 text-center px-8 drop-shadow-sm">{brand.description}</p>
        )}
        {brand.addressLine && (
          <div className="flex items-center gap-1 mt-2 text-white/70 text-xs">
            <MapPin size={12} />
            <span>{brand.addressLine}</span>
          </div>
        )}
      </div>

      {/* ── Status aberto/fechado ──
          12/07/2026 (revisão de design): o backend já calcula isso
          (storeOpen/storeHoursMessage em getPublicCatalog -- inclusive é o
          mesmo horário que foi ajustado nesta sessão) mas o front nunca
          mostrava em lugar nenhum -- o cliente só descobria que a loja
          tava fechada ao tentar falar com o bot. Vira o item de maior
          destaque da faixa de informações, o resto (horário completo,
          taxa, mínimo) fica de apoio abaixo, mais discreto. */}
      {catalog && (
        <div
          className={`px-4 py-2 text-sm font-semibold flex items-center gap-2 ${
            catalog.storeOpen ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          <span className={`w-2 h-2 rounded-full shrink-0 ${catalog.storeOpen ? 'bg-green-500' : 'bg-red-500'}`} />
          <span>
            {catalog.storeOpen
              ? 'Aberto agora'
              : (catalog.storeHoursMessage || 'Loja fechada no momento')}
          </span>
        </div>
      )}

      {/* ── Info chips ── */}
      {(brand.storeHours || brand.deliveryTimeMin || brand.deliveryFeeText || brand.minimumOrderCents) && (
        <div className="bg-white border-b border-gray-100 px-4 py-3 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-gray-700">
          {brand.storeHours && (
            <div className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{brand.storeHours}</span>
            </div>
          )}
          {(storeConfig?.estimatedTimeMin || brand.deliveryTimeMin) && (
            <div className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>{storeConfig?.estimatedTimeMin ?? brand.deliveryTimeMin}{(storeConfig?.estimatedTimeMax || brand.deliveryTimeMax) ? `–${storeConfig?.estimatedTimeMax ?? brand.deliveryTimeMax}` : ''} min</span>
            </div>
          )}
          {(storeConfig?.deliveryFeeCents !== undefined || brand.deliveryFeeText) && (
            <div className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Entrega: {storeConfig ? (storeConfig.deliveryFeeCents === 0 ? 'Grátis' : (storeConfig.freeDeliveryAboveCents ? `Grátis acima de ${fmt(storeConfig.freeDeliveryAboveCents)}` : fmt(storeConfig.deliveryFeeCents))) : brand.deliveryFeeText}</span>
            </div>
          )}
          {(storeConfig?.minOrderCents || brand.minimumOrderCents) && (
            <div className="flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span>Mínimo: {fmt(storeConfig?.minOrderCents ?? brand.minimumOrderCents!)}</span>
            </div>
          )}
          {brand.whatsappNumber && (
            <a
              href={`https://wa.me/${brand.whatsappNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-green-600 font-medium ml-auto"
            >
              <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.555 4.12 1.527 5.854L0 24l6.335-1.527A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.818 9.818 0 01-4.964-1.352l-.356-.213-3.762.907.947-3.667-.233-.373A9.818 9.818 0 1112 21.818z" />
              </svg>
              WhatsApp
            </a>
          )}
        </div>
      )}

      {/* ── Announcement banner ── */}
      {storeConfig?.announcement && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2.5 flex items-start gap-2">
          <span className="text-base leading-tight shrink-0">📢</span>
          <p className="text-sm text-yellow-800 font-medium">{storeConfig.announcement}</p>
        </div>
      )}

      {/* ── Busca + Category Nav (bloco sticky unico) ── */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar produto..."
              aria-label="Buscar produto"
              className="w-full min-h-[44px] pl-9 pr-11 py-2 bg-white border border-gray-200 rounded-xl text-base focus:outline-none focus:border-gray-400"
            />
            {search !== '' && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Limpar busca"
                className="absolute right-0 top-0 bottom-0 w-11 flex items-center justify-center text-gray-400 active:opacity-60"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>
        {filteredCatalog.length > 0 && (
        <div className="relative">
          <div
            ref={navRef}
            className="flex gap-2 overflow-x-auto px-4 pb-2 no-scrollbar"
          >
            {(catalog?.categories ?? []).map((cat) => (
              <button
                key={cat.id}
                data-cat={cat.id}
                onClick={() => scrollToCategory(cat.id)}
                style={activeCategory === cat.id ? { backgroundColor: brand.primaryColor, color: '#fff' } : {}}
                className={`flex-shrink-0 inline-flex items-center min-h-[44px] px-3.5 rounded-full text-[13px] font-semibold transition-colors ${
                  activeCategory === cat.id ? '' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent" />
        </div>
        )}
      </div>

      {/* ── Destaques / Novidades (slides) ── */}
      {/* 13/07/2026 (a pedido do Thales: "slides" pra produtos em promoção
          ou novidade, ex. "Jack com Coca que chegou hoje") -- curadoria
          manual via badge "Destaque" no catálogo do PDV (Product.isHighlight,
          ver delivery.service.ts). Carrossel horizontal com scroll-snap,
          visualmente distinto do grid de categorias normais, posicionado
          acima da seção "Mais Vendidos" (pedido explícito do Thales). Só
          renderiza se houver pelo menos 1 produto marcado. */}
      {(catalog?.highlightProducts?.length ?? 0) > 0 && search === '' && (
        <div className="px-4 pt-2 pb-1">
          <div className="flex items-center gap-1.5 mb-3">
            <Sparkles size={16} style={{ color: brand.accentColor }} />
            <h2 className="text-base font-bold text-gray-900">Novidades & Destaques</h2>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 -mx-4 px-4 no-scrollbar snap-x snap-mandatory">
            {catalog!.highlightProducts!.map((product) => (
              <div
                key={product.id}
                onClick={() => navigate(`/${slug}/produto/${product.id}`)}
                role="button"
                aria-label={`Ver detalhes de ${product.name}`}
                className="flex-shrink-0 w-[168px] snap-start bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden cursor-pointer"
              >
                <div className="w-full h-32 bg-white flex items-center justify-center overflow-hidden relative">
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt={product.name} loading="lazy" decoding="async" className="w-full h-full object-contain p-2" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Sparkles className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                  <span style={{ backgroundColor: brand.accentColor }} className="absolute top-1.5 left-1.5 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                    <Sparkles size={9} /> Destaque
                  </span>
                </div>
                <div className="p-2.5">
                  <p className="text-xs font-semibold text-gray-800 leading-tight line-clamp-2 mb-1.5">{product.name}</p>
                  {product.hasCustomDeliveryPrice && product.deliveryPriceCents < product.salePriceCents ? (
                    <div className="flex items-baseline gap-1 flex-wrap">
                      <span className="text-[10px] text-gray-400 line-through">{fmt(product.salePriceCents)}</span>
                      <span style={{ color: brand.accentColor }} className="text-sm font-bold">{fmt(product.deliveryPriceCents)}</span>
                    </div>
                  ) : (
                    <p style={{ color: brand.accentColor }} className="text-sm font-bold">{fmt(product.deliveryPriceCents)}</p>
                  )}
                  {/* 13/07/2026 (pedido do Thales, "a tag de desconto na foto,
                      aquela do foguinho"): mesmo selo de desconto progressivo
                      (🔥 leve mais, pague menos) que já aparece no ProductCard
                      do grid normal -- faltava aqui porque o card do
                      carrossel de Destaques é um layout próprio, não reusa o
                      ProductCard. */}
                  {product.bulkPricing && product.bulkPricing.length > 0 && (
                    <p className="text-xs font-semibold text-amber-700 leading-tight mt-1">
                      🔥 {product.bulkPricing[product.bulkPricing.length - 1].qty}
                      {product.bulkPricing[product.bulkPricing.length - 1].qty === 4 ? '+' : ''} por{' '}
                      {fmt(product.bulkPricing[product.bulkPricing.length - 1].unitPriceCents)}/un
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Catalog ── */}
      <div className="px-4 space-y-6">
        {filteredCatalog.map((category) => {
          // 12/07/2026 (revisão de design): categoria grande (tem gente com
          // 30+ produtos) fazia a página rolar demais antes do cliente
          // chegar na próxima categoria -- colapsa além de
          // COLLAPSE_THRESHOLD, com "ver mais". Nunca colapsa durante busca
          // (search !== '') -- resultado de busca tem que aparecer inteiro.
          const isExpanded = expandedCategories.has(category.id);
          const shouldCollapse = search === '' && !isExpanded && category.products.length > COLLAPSE_THRESHOLD;
          const visibleProducts = shouldCollapse ? category.products.slice(0, COLLAPSE_THRESHOLD) : category.products;
          const hiddenCount = category.products.length - visibleProducts.length;
          return (
            <div
              key={category.id}
              ref={(el) => { categoryRefs.current[category.id] = el; }}
              className="scroll-mt-[116px]"
            >
              <div className="mb-3 pt-2">
                <h2 className="text-base font-bold text-gray-900">{category.name}</h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {visibleProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    brand={brand}
                    onOpen={() => navigate(`/${slug}/produto/${product.id}`)}
                  />
                ))}
              </div>
              {hiddenCount > 0 && (
                <button
                  onClick={() => setExpandedCategories((prev) => new Set(prev).add(category.id))}
                  className="w-full mt-3 py-2.5 text-sm font-semibold rounded-xl border border-gray-200 text-gray-600 active:bg-gray-50"
                >
                  Ver mais {hiddenCount} produtos
                </button>
              )}
            </div>
          );
        })}
        {filteredCatalog.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
            <p className="text-3xl">🔍</p>
            <p className="text-gray-500 text-sm">Nenhum produto encontrado</p>
          </div>
        )}
      </div>

      {/* ── Footer ── */}
      <div className="text-center py-6">
        <a
          href="https://autocobranca.com.br/delivery-admin/login"
          className="text-xs text-gray-300 hover:text-gray-500 transition-colors"
        >
          Gerenciar loja
        </a>
      </div>

      {/* ── Cart FAB ── */}
      <CartFAB brand={brand} onClick={() => setCartOpen(true)} />

      {/* ── Cart Sheet ── */}
      <CartSheet
        open={cartOpen}
        brand={brand}
        onClose={() => setCartOpen(false)}
        onCheckout={() => { setCartOpen(false); setCheckoutOpen(true); }}
        categories={catalog?.categories ?? []}
        categoryAffinity={catalog?.categoryAffinity}
      />

      {/* ── Checkout Modal ── */}
      {checkoutOpen && (
        <CheckoutModal
          open={checkoutOpen}
          brand={brand}
          slug={slug!}
          onClose={() => setCheckoutOpen(false)}
          onSuccess={(id) => { setCheckoutOpen(false); setConfirmedOrderId(id); }}
        />
      )}
    </div>
    </div>
  );
}
